import React, { useEffect, useState } from 'react';
import { HomeLayouts } from '../../../components/layouts/HomeLayout';
import Loading from '../../../components/Loading/Loading';
import { OutlinedInput, InputAdornment } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { IoArrowBackOutline as Back } from '@react-icons/all-files/io5/IoArrowBackOutline';
import { useSelector } from 'react-redux';
import { quoteForId, quoteUpdate, quoteNoteUpdate } from '../../../api/quotes';
import moment from 'moment';
import PopUp from '../../../components/PopUp';
import CancelQuote from '../../../components/PopUp/components/CancelQuote/CancelQuote';
import { getDisplayDate } from '../../../utils/dates';
import toast from 'react-hot-toast';
import styles from './quoteDetail.module.scss';

// ── Product image with green letter fallback ─────────────────────
const ProductImage = ({ image, name, onExpand }) => {
  const [imgError, setImgError] = useState(false);
  const initial = name ? name.charAt(0).toUpperCase() : '?';
  const isImage = image && !imgError;

  const handleClick = () => {
    if (onExpand) onExpand({ isImage, src: image, name, initial });
  };

  if (isImage) {
    return (
      <div className={styles.productImageWrap} onClick={handleClick}>
        <img
          src={image}
          alt={name}
          className={styles.productImage}
          onError={() => setImgError(true)}
        />
      </div>
    );
  }
  return (
    <div className={styles.productImageWrap} onClick={handleClick}>
      <div className={styles.productPlaceholder}>{initial}</div>
    </div>
  );
};

export const QuotesDetail = () => {
  const { token } = useSelector((state) => state.auth);
  const { id } = useParams();
  const [order, setorder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setloading] = useState(false);
  const [loadingSend, setloadingSend] = useState(false);
  const [complet, setComplet] = useState(false);
  const [noteSupp, setnoteSupp] = useState('');
  const [originalNote, setOriginalNote] = useState('');
  const [expandedImage, setExpandedImage] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadQuote();
  }, []);

  const loadQuote = async () => {
    setloading(true);
    const resp = await quoteForId(token, id);
    if (resp.ok) {
      setorder(resp.data);
      const note = resp.data.noteSupplier ?? '';
      setnoteSupp(note);
      setOriginalNote(note);
    } else {
      setorder(null);
    }
    setloading(false);
  };

  const submit = async (status) => {
    if (status === 'Completada' && order.products[0].price === null) {
      return toast.error('Debes ingresar precio a los productos de la cotización');
    }
    const data = {
      status,
      note: '',
      noteSupplier: noteSupp,
      products: [
        {
          supplierId: order.supplierId.toString(),
          restaurantId: order.restaurantId.toString(),
          productId: order.products[0].productRelatedId.toString(),
          price: status === 'Rechazada' ? 0 : parseFloat(order.products[0].price),
        },
      ],
    };
    if (status === 'Completada') {
      const resp = await quoteUpdate(token, id, data);
      if (resp.ok) navigate(`/cotizaciones/confirmacion_cotizacion/${order.code}`);
      setloadingSend(false);
    } else {
      const resp = await quoteUpdate(token, id, data);
      if (resp.ok) {
        toast.success('Cotización rechazada de manera correcta');
        loadQuote();
      }
    }
  };

  const handleOnChange = (index, event) => {
    const newProducts = order.products.map((p, i) =>
      i === index ? { ...p, price: event.target.value } : p
    );
    setorder((prev) => ({ ...prev, products: newProducts }));
  };

  const handleSaveNote = async () => {
    try {
      const resp = await quoteNoteUpdate(token, id, { noteSupplier: noteSupp });
      if (resp.ok) {
        toast.success('Nota guardada correctamente');
        setOriginalNote(noteSupp);
      } else {
        toast.error('Error al guardar la nota');
      }
    } catch {
      toast.error('Error al guardar la nota');
    }
  };

  const showPrice = order && (order.status === 'Completada' || order.status === 'Aceptada');
  const showPriceInput = order && order.status === 'Solicitada' && complet;
  const isEditable = order && (order.status === 'Solicitada' || order.status === 'Completada');
  const isPending = order && !['Completada', 'Rechazada', 'Aceptada'].includes(order.status);

  return (
    <HomeLayouts>
      {loading || order === null ? (
        <Loading />
      ) : (
        <>
          {/* Image lightbox */}
          {expandedImage && (
            <div className={styles.lightbox} onClick={() => setExpandedImage(null)}>
              {expandedImage.isImage ? (
                <img
                  src={expandedImage.src}
                  alt={expandedImage.name}
                  className={styles.lightboxImg}
                />
              ) : (
                <div className={styles.lightboxPlaceholder}>
                  {expandedImage.initial}
                </div>
              )}
            </div>
          )}

          <PopUp setShowModal={setShowModal} showModal={showModal}>
            <CancelQuote
              setShowModal={setShowModal}
              comerc={order.nameRestaurant}
              suppliuer={order.anonymousRestaurant}
              date={getDisplayDate(order.createdAt)}
              onClick={() => submit('Rechazada')}
            />
          </PopUp>

          <div className={styles.page}>
            <div className={styles.pageInner}>

              {/* ────────────── LEFT PANEL ────────────── */}
              <div className={styles.leftPanel}>

                <div className={styles.backRow} onClick={() => navigate('/cotizaciones')}>
                  <Back className={styles.backIcon} />
                  <span>Volver</span>
                </div>

                <div className={styles.titleBlock}>
                  <h1 className={styles.pageTitle}>Detalle de cotización</h1>
                  <p className={styles.pageSubtitle}>
                    Para{' '}
                    <span className={styles.pageSubtitleHighlight}>
                      {order.anonymousRestaurant ? 'Anónimo' : order.nameRestaurant}
                    </span>
                  </p>
                </div>

                {/* Products */}
                <div className={styles.sectionCard}>
                  <p className={styles.sectionLabel}>Productos cotizados</p>

                  <div className={`${styles.productsHeader} ${showPrice || showPriceInput ? styles.withPrice : ''}`}>
                    <span className={styles.colHeader}>Producto</span>
                    <span className={styles.colHeader}>Cantidad</span>
                    {(showPrice || showPriceInput) && (
                      <span className={styles.colHeader}>Precio</span>
                    )}
                  </div>

                  {order.products.map((product, index) => (
                    <div
                      key={index}
                      className={`${styles.productRow} ${showPrice || showPriceInput ? styles.withPrice : ''}`}
                    >
                      <div className={styles.productInfo}>
                        <ProductImage
                          image={product.image}
                          name={product.name}
                          onExpand={(data) => setExpandedImage(data)}
                        />
                        <span className={styles.productName}>{product.name}</span>
                      </div>

                      <span className={styles.productQty}>
                        {product.quantity} {product.unitOfMeasure}
                      </span>

                      {showPrice && (
                        <span className={styles.productPrice}>
                          ${product.price} / {product.unitOfMeasure}
                        </span>
                      )}

                      {showPriceInput && (
                        <div className={styles.priceInputWrap}>
                          <OutlinedInput
                            onChange={(e) => handleOnChange(index, e)}
                            startAdornment={
                              <InputAdornment sx={{ marginLeft: -1 }} position="start">
                                $
                              </InputAdornment>
                            }
                            size="small"
                            value={product.price === undefined || product.price === null ? '' : product.price}
                            name={product.name}
                            type="number"
                            inputProps={{ style: { paddingRight: 2, height: '10px' } }}
                          />
                          <span className={styles.priceUnit}>
                            /{order.products[0]?.unitOfMeasure}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Client note */}
                <div className={styles.sectionCard}>
                  <div className={styles.noteLabelRow}>
                    <span className={styles.noteLabel}>Nota del cliente</span>
                  </div>
                  <textarea
                    className={styles.noteReadOnly}
                    value={order.note || ''}
                    readOnly
                    placeholder="Sin notas del cliente"
                  />
                </div>

                {/* Supplier note */}
                <div className={styles.sectionCard}>
                  <div className={styles.noteLabelRow}>
                    <span className={styles.noteLabel}>
                      {order.status === 'Solicitada' && complet ? 'Agregar nota' : 'Nota del proveedor'}
                    </span>
                    {noteSupp !== originalNote && isEditable && (
                      <button className={styles.saveNoteBtn} onClick={handleSaveNote}>
                        Guardar
                      </button>
                    )}
                  </div>
                  <textarea
                    className={isEditable ? styles.noteEditable : styles.noteReadOnly}
                    onChange={(e) => setnoteSupp(e.target.value)}
                    value={noteSupp}
                    readOnly={!isEditable}
                    placeholder={isEditable ? 'Escribe una nota para el cliente...' : 'Sin notas del proveedor'}
                  />
                </div>

              </div>

              {/* ────────────── RIGHT PANEL (DARK SIDEBAR) ────────────── */}
              <div className={styles.rightPanel}>

                <div className={styles.statusBlock}>
                  <p className={styles.sidebarSectionLabel}>Estado</p>
                  <span className={`${styles.statusPillLarge} ${styles[order.status]}`}>
                    {order.status}
                  </span>
                </div>

                <div className={styles.sidebarDivider} />

                <div className={styles.sidebarSection}>
                  <p className={styles.sidebarSectionLabel}>Resumen</p>

                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Código</span>
                    <span className={`${styles.summaryValue} ${styles.summaryCode}`}>
                      {order.code}
                    </span>
                  </div>

                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Cliente</span>
                    <span className={styles.summaryValue}>
                      {order.anonymousRestaurant ? 'Anónimo' : order.nameRestaurant}
                    </span>
                  </div>

                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Fecha</span>
                    <span className={styles.summaryValue}>
                      {moment(order.createdAt).format('DD MMM YYYY')}
                    </span>
                  </div>

                  {order.responseDate && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Respondida</span>
                      <span className={styles.summaryValue}>
                        {moment(order.responseDate).format('DD MMM YYYY')}
                      </span>
                    </div>
                  )}
                </div>

                {(order.restaurantContactName || order.restaurantPhone) && (
                  <>
                    <div className={styles.sidebarDivider} />
                    <div className={styles.sidebarSection}>
                      <p className={styles.sidebarSectionLabel}>Contacto cliente</p>

                      {order.restaurantContactName && (
                        <div className={styles.summaryRow}>
                          <span className={styles.summaryKey}>Nombre</span>
                          <span className={styles.summaryValue}>{order.restaurantContactName}</span>
                        </div>
                      )}

                      {order.restaurantPhone && (
                        <div className={styles.summaryRow}>
                          <span className={styles.summaryKey}>Teléfono</span>
                          <span className={styles.summaryValue}>{order.restaurantPhone}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {isPending && (
                  <div className={styles.actionsWrap}>
                    <button
                      className={styles.btnPrimary}
                      disabled={loadingSend}
                      onClick={() => (!complet ? setComplet(true) : submit('Completada'))}
                    >
                      {complet ? 'Confirmar cotización' : 'Completar cotización'}
                    </button>

                    {!complet && (
                      <button
                        className={styles.btnDanger}
                        disabled={loadingSend}
                        onClick={() => setShowModal(true)}
                      >
                        Rechazar cotización
                      </button>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </>
      )}
    </HomeLayouts>
  );
};
